import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "redux";
import axios from "../utils/axios.tsx";
import type { FeatureFlagsState } from "./types.tsx";

const initialState: FeatureFlagsState = {
  flags: [],
  loading: false,
  loaded: false,
  error: null,
};

const featureFlagsSlice = createSlice({
  name: "featureFlags",
  initialState,
  reducers: {
    setFeatureFlags(state, action) {
      state.flags = action.payload;
      state.loading = false;
      state.loaded = true;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
      state.loaded = true;
    },
  },
});

export const featureFlagsActions = featureFlagsSlice.actions;

// A failed fetch used to leave the flags empty for the rest of the session,
// hiding every flagged feature until a reload. A short retry covers the usual
// cause: a request that lands while the API is restarting.
const RETRY_DELAYS_MS = [1000, 3000];

const wait = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

export const fetchFeatureFlags = () => {
  return async (dispatch: Dispatch) => {
    dispatch(featureFlagsActions.setLoading(true));
    let lastError = "Failed to fetch feature flags";

    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) await wait(RETRY_DELAYS_MS[attempt - 1]);
      try {
        const response = await axios.get("/v1/feature_flag/", {
          withCredentials: true,
        });

        if (response.status === 200) {
          dispatch(featureFlagsActions.setFeatureFlags(response.data));
          return;
        }
        // Signed out meanwhile: retrying would only 401 again.
        if (response.status === 401) break;
      } catch (error) {
        console.error("Error fetching feature flags:", error);
        lastError = error instanceof Error ? error.message : "Unknown error";
      }
    }

    dispatch(featureFlagsActions.setError(lastError));
  };
};

export default featureFlagsSlice.reducer;
